
import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BrandingSettings } from "@/components/settings/BrandingSettings";
import { XeroIntegration } from "@/components/settings/XeroIntegration";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CirclePlus, Save, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

export function SettingsForm() {
  const { user, profile, updateUser } = useAuth();
  const { toast } = useToast();
  
  const [userFormData, setUserFormData] = useState({
    name: profile?.name || "",
    email: profile?.email || "",
    profileImage: profile?.profileImage || "",
  });
  
  const [imagePreview, setImagePreview] = useState<string | undefined>(profile?.profileImage);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserFormData({
      ...userFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        setImagePreview(result);
        setUserFormData({
          ...userFormData,
          profileImage: result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const saveProfile = () => {
    updateUser(userFormData);
    toast({
      title: "Profile Updated",
      description: "Your profile information has been updated.",
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div>
      <h1 className="text-2xl font-bold mb-6">Settings</h1>
      
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="branding">Branding</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="preferences">Preferences</TabsTrigger>
        </TabsList>
        
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">User Profile</CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-col md:flex-row gap-4 items-start">
                <div className="flex flex-col items-center gap-2">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={imagePreview} alt={userFormData.name} />
                    <AvatarFallback className="text-lg">
                      {getInitials(userFormData.name)}
                    </AvatarFallback>
                  </Avatar>
                  <Label htmlFor="profile-image" className="cursor-pointer">
                    <div className="flex items-center text-xs text-blue-600 hover:text-blue-800">
                      <CirclePlus className="mr-1 h-3 w-3" />
                      <span>Change</span>
                    </div>
                    <Input 
                      id="profile-image"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageChange}
                    />
                  </Label>
                </div>
                
                <div className="flex-1 space-y-4 w-full">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center">
                      <User className="h-4 w-4 mr-2" />
                      Name
                    </Label>
                    <Input 
                      id="name" 
                      name="name"
                      value={userFormData.name} 
                      onChange={handleChange} 
                      placeholder="Your Name"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input 
                      id="email" 
                      name="email"
                      type="email"
                      value={userFormData.email} 
                      onChange={handleChange} 
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <Button onClick={saveProfile}>
                    <Save className="h-4 w-4 mr-2" />
                    Save Profile
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="branding">
          <BrandingSettings />
        </TabsContent>
        
        <TabsContent value="integrations">
          <XeroIntegration />
        </TabsContent>
        
        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl font-semibold">User Preferences</CardTitle>
              <CardDescription>
                Customize your application experience
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">Preferences will be added in a future update.</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
